import { useEffect, useState } from "react";
import { BookOpen, Play, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import LearnMode from "./reading/LearnMode";
import TestMode from "./reading/TestMode";

// A single uniform rule tag isn't a real sub-division — mirrors
// backend/modules_db.py::derive_sections (only 2+ distinct values count).
function deriveSections(m) {
  if (m.sections) return m.sections;
  const seen = new Set(); const order = [];
  for (const it of m.items) {
    const r = it.rule;
    if (r && !seen.has(r)) { seen.add(r); order.push(r); }
  }
  return order.length > 1 ? order : [];
}

/**
 * Reading mode — modules 1-7. Exactly two top-level options: Learn and Test.
 *
 * Section is a single shared selector (when the module has sections) that
 * sits above both — Learn and Test always show the same section's content
 * and never drift out of sync, unlike the old design where each mode kept
 * its own independent section picker.
 */
export default function ReadingMode({ module: m, progress, onAttemptSaved }) {
  const { t } = useI18n();
  const [topMode, setTopMode] = useState("learn"); // "learn" | "test"
  // While a test is actively running (timer/recording in progress), switching
  // away would silently drop the recording — so navigation is locked until
  // the attempt finishes or is reset.
  const [testRunning, setTestRunning] = useState(false);

  const sections = deriveSections(m);
  const hasSections = sections.length > 0;
  const [selectedSection, setSelectedSection] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(`qf:section:${m.code}`) : null;
    return sections.includes(saved) ? saved : (sections[0] || null);
  });
  // A section is "unlocked" for Test the moment its Learn tab has been
  // opened at least once — no per-item completion tracking, just visiting.
  const [visitedSections, setVisitedSections] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(`qf:learn-visited:${m.code}`) : null;
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  useEffect(() => {
    if (topMode !== "learn" || !selectedSection || visitedSections.has(selectedSection)) return;
    const next = new Set(visitedSections);
    next.add(selectedSection);
    setVisitedSections(next);
    localStorage.setItem(`qf:learn-visited:${m.code}`, JSON.stringify([...next]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topMode, selectedSection]);

  const selectSection = (s) => {
    setSelectedSection(s);
    localStorage.setItem(`qf:section:${m.code}`, s);
  };

  const sectionUnlocked = !hasSections || visitedSections.has(selectedSection);

  return (
    <>
      {hasSections && (
        <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-sm p-3 mb-4" data-testid="shared-section-tabs">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#4B5563] mb-2 px-1">{t("rd.section_picker_label")}</div>
          <div className="flex flex-wrap gap-2">
            {sections.map((s) => {
              const active = selectedSection === s;
              const visited = visitedSections.has(s);
              const passed = Boolean(progress?.sections?.[s]?.passed);
              return (
                <button key={s} data-testid={`shared-section-tab-${s}`}
                  onClick={() => !testRunning && selectSection(s)}
                  disabled={testRunning}
                  title={testRunning ? "Finish or reset the current test first" : undefined}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    active ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white text-[#1F2937] border-[#E7E0D2] hover:border-[#064E3B]"
                  }`}>
                  {passed ? (
                    <span className="inline-block w-2 h-2 rounded-full bg-[#10B981]" title={t("cert.status_passed")} />
                  ) : visited ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : null}
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-sm p-3 sm:p-4" data-testid="top-mode-toggle">
        <div className="flex items-center gap-2">
          <button
            data-testid="top-mode-learn"
            onClick={() => !testRunning && setTopMode("learn")}
            disabled={testRunning}
            title={testRunning ? "Finish or reset the current test first" : undefined}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
              topMode === "learn" ? "bg-[#D97706] text-white border-[#D97706]" : "bg-white border-[#E7E0D2] hover:border-[#D97706] text-[#1F2937]"
            }`}>
            <BookOpen className="w-4 h-4" /> {t("rd.learn")}
          </button>
          <button
            data-testid="top-mode-test"
            onClick={() => !testRunning && setTopMode("test")}
            disabled={testRunning}
            title={testRunning ? "Finish or reset the current test first" : undefined}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
              topMode === "test" ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]"
            }`}>
            <Play className="w-4 h-4" /> {t("rd.test")}
          </button>
        </div>
      </div>

      <div className="mt-4">
        {topMode === "learn"
          ? <LearnMode module={m} hasSections={hasSections} selectedSection={selectedSection} />
          : <TestMode module={m} progress={progress} onAttemptSaved={onAttemptSaved} onRunningChange={setTestRunning}
              hasSections={hasSections} selectedSection={selectedSection} sectionUnlocked={sectionUnlocked} />}
      </div>
    </>
  );
}
